import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  VolumeX,
  Repeat,
  Repeat1,
  Maximize2,
  Minimize2,
  Music,
  TrendingUp,
  Keyboard
} from 'lucide-react';
import { cn } from '@/lib/utils';
import ShortcutsDialog from './ShortcutsDialog';

const Player = React.memo(function Player({
  currentSong,
  isPlaying,
  currentTime,
  duration,
  volume,
  isLooping,
  isPlaylistLooping,
  isMinimized,
  isStreamerMode,
  handlePlayPause, 
  onSeek,
  onVolumeChange,
  onToggleLoop,
  onTogglePlaylistLoop,
  onNext,
  onPrevious,
  onToggleMinimize,
  normalizationApplied,
  showNormalizationIndicator
}) {
  const [isMuted, setIsMuted] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(volume);
  const [showShortcuts, setShowShortcuts] = useState(false);

  useEffect(() => {
    if (!isMuted) {
      setPreviousVolume(volume);
    }
  }, [volume, isMuted]);

  const formatTime = (time) => {
    if (time === null || typeof time === 'undefined' || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleVolumeToggle = () => {
    if (isMuted) {
      onVolumeChange(previousVolume);
      setIsMuted(false);
    } else {
      setPreviousVolume(volume); 
      onVolumeChange(0);
      setIsMuted(true);
    }
  };

  const handleVolumeSliderChange = (newVolume) => { 
    onVolumeChange(newVolume[0]);
    setIsMuted(newVolume[0] === 0);
    if (newVolume[0] > 0 && isMuted) { 
        setPreviousVolume(newVolume[0]); 
    }
  };

  const handlePlayPauseClick = async () => {
    console.log('🎯 Player: Play button clicked. Current song:', currentSong);
    
    if (!currentSong || !currentSong.file) {
      console.log('❌ Player: No file available or no current song.');
      return;
    }
    
    console.log('🎯 Player: Calling handlePlayPause...'); 
    await handlePlayPause(); 
  };

  const progress = duration > 0 && !isNaN(duration) && !isNaN(currentTime) ? (currentTime / duration) * 100 : 0;
  const canPlay = !!(currentSong && currentSong.file);


  if (isMinimized) {
    return (
      <div className={`h-full relative ${isStreamerMode ? 'bg-black' : ''}`}>
        <AnimatePresence>
          {showNormalizationIndicator && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className={`absolute top-0 left-0 right-0 bg-gradient-to-r from-green-500/20 to-blue-500/20 backdrop-blur-sm border-b border-green-400/30 z-10 ${
                isStreamerMode ? 'bg-black/80 px-3 py-1' : 'px-4 py-2'
              }`}
            >
              <div className={`flex items-center justify-center gap-2 ${isStreamerMode ? 'text-xs' : 'text-sm'}`}>
                <TrendingUp className={`text-green-400 ${isStreamerMode ? 'h-3 w-3' : 'h-4 w-4'}`} />
                <span className="text-green-400 font-medium">
                  Volume Normalization: {normalizationApplied}%
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className={`h-full flex items-center justify-between ${isStreamerMode ? 'px-4' : 'px-6'}`}>
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="min-w-0 flex-1">
              <p className={`font-medium truncate text-white ${isStreamerMode ? 'text-xs' : 'text-sm'}`}>
                {currentSong?.title || currentSong?.name || 'No song selected'}
              </p>
              <p className={`text-white/60 truncate ${isStreamerMode ? 'text-xs' : 'text-xs'}`}>
                {currentSong?.artist || 'Unknown Artist'}
              </p>
            </div>
          </div>

          <div className={`flex items-center ${isStreamerMode ? 'gap-1' : 'gap-2'}`}>
            <Button
              size="sm"
              variant="ghost"
              onClick={onPrevious}
              className={`text-white hover:bg-white/10 ${isStreamerMode ? 'h-7 w-7 p-0' : ''}`}
              disabled={!canPlay}
            >
              <SkipBack className={`text-white ${isStreamerMode ? 'h-3 w-3' : 'h-4 w-4'}`} />
            </Button>
            
            <button
              onClick={handlePlayPauseClick}
              className={`rounded-full flex items-center justify-center shadow-lg cursor-pointer transition-all duration-200 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed border-0 ${
                isStreamerMode ? 'w-8 h-8' : 'w-10 h-10'
              }`}
              disabled={!canPlay}
            >
              {isPlaying ? (
                <Pause className={`text-white fill-white ${isStreamerMode ? 'h-3 w-3' : 'h-4 w-4'}`} />
              ) : (
                <Play className={`ml-0.5 text-white fill-white ${isStreamerMode ? 'h-3 w-3' : 'h-4 w-4'}`} />
              )}
            </button>
            
            <Button
              size="sm"
              variant="ghost"
              onClick={onNext}
              className={`text-white hover:bg-white/10 ${isStreamerMode ? 'h-7 w-7 p-0' : ''}`}
              disabled={!canPlay}
            >
              <SkipForward className={`text-white ${isStreamerMode ? 'h-3 w-3' : 'h-4 w-4'}`} />
            </Button>

            <Button
              size="sm"
              variant="ghost"
              onClick={onToggleMinimize}
              className={`text-white hover:bg-white/10 ${isStreamerMode ? 'h-7 w-7 p-0' : ''}`}
            >
              <Maximize2 className={`text-white ${isStreamerMode ? 'h-3 w-3' : 'h-4 w-4'}`} />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`h-full flex flex-col justify-center relative ${isStreamerMode ? 'bg-black px-4 py-2' : 'px-6'}`}>
        <AnimatePresence>
          {showNormalizationIndicator && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className={`absolute top-0 left-0 right-0 bg-gradient-to-r from-green-500/20 to-blue-500/20 backdrop-blur-sm border-b border-green-400/30 z-10 rounded-t-lg ${
                isStreamerMode ? 'bg-black/80 px-4 py-2' : 'px-6 py-3'
              }`}
            >
              <div className={`flex items-center justify-center gap-2 ${isStreamerMode ? 'text-sm' : ''}`}>
                <TrendingUp className={`text-green-400 ${isStreamerMode ? 'h-4 w-4' : 'h-5 w-5'}`} />
                <span className="text-green-400 font-medium">
                  Volume Normalization: {normalizationApplied}%
                </span>
                {!isStreamerMode && (
                  <div className="ml-2 text-xs text-green-300/80">
                    Tracks balanced for consistent volume
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className={`${isStreamerMode ? 'mb-2' : 'mb-4'}`}>
          <Slider
            value={[progress]}
            onValueChange={(value) => {
              if (duration > 0 && !isNaN(duration)) {
                onSeek((value[0] / 100) * duration);
              }
            }}
            max={100}
            step={0.1}
            className="w-full"
            disabled={!canPlay || !(duration > 0)}
          />
          <div className={`flex justify-between text-white/60 mt-1 ${isStreamerMode ? 'text-xs' : 'text-xs'}`}>
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        <div className={`flex items-center justify-center ${isStreamerMode ? 'gap-4' : 'gap-8'}`}>
          <div className={`flex items-center gap-3 flex-1 min-w-0 ${isStreamerMode ? 'max-w-[200px]' : ''}`}>
            <div className={`bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0 ${
              isStreamerMode ? 'w-12 h-12' : 'w-16 h-16'
            }`}>
              {currentSong?.artwork ? (
                <img 
                  src={currentSong.artwork} 
                  alt="Album art" 
                  className="w-full h-full object-cover rounded-xl"
                />
              ) : (
                <Music className={`text-white ${isStreamerMode ? 'h-6 w-6' : 'h-8 w-8'}`} />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <p className={`font-medium truncate text-white ${isStreamerMode ? 'text-sm' : ''}`}>
                {currentSong?.title || currentSong?.name || 'No song selected'}
              </p>
              <p className={`text-white/60 truncate ${isStreamerMode ? 'text-xs' : 'text-sm'}`}>
                {currentSong?.artist || 'Unknown Artist'}
              </p>
              {currentSong && !currentSong.file && (
                <p className="text-xs text-red-400">File not available</p>
              )}
            </div>
          </div>

          <div className={`flex items-center ${isStreamerMode ? 'gap-2' : 'gap-4'}`}>
            <Button
              variant="ghost"
              size="sm"
              onClick={onPrevious}
              className={`text-white hover:bg-white/10 ${isStreamerMode ? 'h-8 w-8 p-0' : ''}`}
              disabled={!canPlay}
            >
              <SkipBack className={`text-white ${isStreamerMode ? 'h-4 w-4' : 'h-5 w-5'}`} />
            </Button>
            
            <button
              onClick={handlePlayPauseClick}
              className={`rounded-full flex items-center justify-center shadow-lg cursor-pointer transition-all duration-200 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed border-0 ${
                isStreamerMode ? 'w-10 h-10' : 'w-14 h-14'
              }`}
              disabled={!canPlay}
            >
              {isPlaying ? (
                <Pause className={`text-white fill-white ${isStreamerMode ? 'h-4 w-4' : 'h-6 w-6'}`} />
              ) : (
                <Play className={`ml-0.5 text-white fill-white ${isStreamerMode ? 'h-4 w-4' : 'h-6 w-6'}`} />
              )}
            </button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={onNext}
              className={`text-white hover:bg-white/10 ${isStreamerMode ? 'h-8 w-8 p-0' : ''}`}
              disabled={!canPlay}
            >
              <SkipForward className={`text-white ${isStreamerMode ? 'h-4 w-4' : 'h-5 w-5'}`} />
            </Button>
          </div>

          <div className={`flex items-center flex-1 justify-end ${isStreamerMode ? 'gap-1 max-w-[200px]' : 'gap-3'}`}>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleLoop}
              className={cn(
                'text-white hover:bg-white/10',
                isLooping && 'bg-blue-500/30 hover:bg-blue-500/40',
                isStreamerMode ? 'h-7 w-7 p-0' : ''
              )}
              title="Loop current song"
              disabled={!canPlay}
            >
              <Repeat1 className={cn('transition-colors', isLooping ? 'text-blue-400' : 'text-white', isStreamerMode ? 'h-3 w-3' : 'h-4 w-4')} />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={onTogglePlaylistLoop}
              className={cn(
                'text-white hover:bg-white/10',
                isPlaylistLooping && 'bg-blue-500/30 hover:bg-blue-500/40',
                isStreamerMode ? 'h-7 w-7 p-0' : ''
              )}
              title="Loop playlist"
              disabled={!canPlay}
            >
              <Repeat className={cn('transition-colors', isPlaylistLooping ? 'text-blue-400' : 'text-white', isStreamerMode ? 'h-3 w-3' : 'h-4 w-4')} />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowShortcuts(true)}
              className={cn('text-white hover:bg-white/10', isStreamerMode ? 'h-7 w-7 p-0' : '')}
              title="Show Keyboard Shortcuts"
            >
              <Keyboard className={cn('mr-1', isStreamerMode ? 'h-3 w-3' : 'h-4 w-4')} />
              <span className="hidden md:inline">Shortcuts</span>
            </Button>

            <div className={`flex items-center ${isStreamerMode ? 'gap-1' : 'gap-2'}`}>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleVolumeToggle}
                className={`text-white hover:bg-white/10 ${isStreamerMode ? 'h-7 w-7 p-0' : ''}`}
              >
                {isMuted || volume === 0 ? (
                  <VolumeX className={`text-white ${isStreamerMode ? 'h-3 w-3' : 'h-4 w-4'}`} />
                ) : (
                  <Volume2 className={`text-white ${isStreamerMode ? 'h-3 w-3' : 'h-4 w-4'}`} />
                )}
              </Button>
              
              <div className={isStreamerMode ? 'w-16' : 'w-20'}>
                <Slider
                  value={[isMuted ? 0 : volume * 100]}
                  onValueChange={(value) => handleVolumeSliderChange([value[0] / 100])}
                  max={100}
                  step={1}
                  className="w-full"
                />
              </div>
              <span className="ml-2 text-xs text-white/70 min-w-[32px] text-right select-none">
                {`${Math.round((isMuted ? 0 : volume) * 100)}%`}
              </span>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleMinimize}
              className={`text-white hover:bg-white/10 ${isStreamerMode ? 'h-7 w-7 p-0' : ''}`}
            >
              <Minimize2 className={`text-white ${isStreamerMode ? 'h-3 w-3' : 'h-4 w-4'}`} />
            </Button>
          </div>
        </div>

        {showShortcuts && createPortal(
          <ShortcutsDialog open={showShortcuts} onClose={() => setShowShortcuts(false)} />,
          document.body
        )}
      </div>
    </>
  );
});

export { Player };